/**
 * SSML テンプレート入力欄の凡例 (`PlaceholderLegend`) に渡すプレースホルダ定義。
 *
 * 分割前は `components/BroadcastContentsEditor.tsx` の 7 か所に散在していたものを
 * 1 ファイルに集約した。トークン・説明文はすべて元のまま (文言変更なし)。
 */
import type { PlaceholderItem } from './types';

/**
 * 自列車遅延の「放送パターン (SSMLテンプレート)」入力欄で使えるプレースホルダ一覧。
 * 凡例 UI (`PlaceholderLegend`) に渡してトークン + 簡易説明として表示する。
 */
export const SELF_TRAIN_DELAY_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
  { token: '{route_name}', description: '自路線の一般的な線名 (未登録時は {route_id} の値)' },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
];

/**
 * 接続線遅延 (transfer_aware モード) の「放送パターン (SSMLテンプレート)」入力欄で
 * 使えるプレースホルダ一覧。乗換駅名 (`{from_station}`) と方面 (`{direction_name}`) が
 * 自列車遅延と違って利用できる点が特徴。
 */
export const CONNECTING_TRANSFER_AWARE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{from_station}', description: '乗換駅名' },
  { token: '{from_prefix}', description: '「<駅名>駅から」(乗換駅が無ければ空欄)' },
  { token: '{direction_name}', description: '方面 (末尾に「方面行き」が自動付与)' },
  { token: '{route_name}', description: '接続線の一般的な線名 (未登録時は {route_id} の値)' },
  { token: '{route_id}', description: 'ATOS 線名コード (路線ID)' },
];

/**
 * 接続線遅延 (whole_line モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 *
 * whole_line モードでは Lambda 側で乗換駅・方面が放送されないため、
 * `{from_prefix}` / `{from_station}` / `{direction_name}` は凡例から除外し、
 * 注釈 (note) として「空欄として扱われます」を凡例の下に明示する。
 */
export const CONNECTING_WHOLE_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
];

/**
 * 接続線遅延 (section_scheduled モード) の単一 SSML テンプレートで使えるプレースホルダ一覧。
 * 乗換駅 (`{from_station}` / `{from_prefix}`) と方面 (`{direction_name}`) も
 * 利用可能 (Lambda 側で展開される)。
 */
export const CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '一般的な線名 (例: 京浜東北線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 京浜)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  { token: '{from_station}', description: '乗換駅名' },
  { token: '{from_prefix}', description: '「<駅名>駅からお」(乗換駅が無ければ空欄)' },
  { token: '{direction_name}', description: '方面 (末尾に「方面行き」が自動付与)' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
];

// ---------------------------------------------------------------------------
// 接続線遅延 (乗換駅ベース / part2) 用のプレースホルダ凡例定数。
// ---------------------------------------------------------------------------

/**
 * 冒頭 SSML テンプレート (`ssml_intro_template`) で使えるプレースホルダ一覧。
 * 乗換駅名のみを扱うシンプルな凡例。
 */
export const CONNECTING_STATION_INTRO_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)' },
];

/**
 * 接続線ブロック SSML テンプレート (`ssml_line_template`) で使えるプレースホルダ一覧。
 * 方面部分文を読点連結した文字列 `{directions_text}` を必ず含めることを想定する。
 */
export const CONNECTING_STATION_LINE_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{route_name}', description: '接続線の一般的な線名 (例: 埼京線)' },
  { token: '{route_id}', description: 'ATOS 線名コード (例: 埼京)' },
  { token: '{route}', description: '{route_name} と同じ意味' },
  {
    token: '{directions_text}',
    description: '方面部分文を読点で連結した文字列 (方面ごとの遅延案内)',
  },
  { token: '{transfer_station}', description: '乗換駅名 (例: 赤羽)' },
];

/**
 * 方面部分文 SSML テンプレート (`lines[j].delay_patterns[k].ssml_template`) で
 * 使えるプレースホルダ一覧。part1 の transfer_aware 用凡例に近いが、乗換駅
 * (`{from_station}` / `{from_prefix}`) は使わず、代わりに `{transfer_station}` を使う。
 */
export const CONNECTING_STATION_DIRECTION_PLACEHOLDERS: PlaceholderItem[] = [
  { token: '{direction_name}', description: '方面名 (例: 新宿)。必須項目' },
  { token: '{delay_minutes_1}', description: '1分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_5}', description: '5分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes_10}', description: '10分単位で切り捨てた遅延分数' },
  { token: '{delay_minutes}', description: '切り上げ・最低1分の遅延分数' },
  { token: '{delay_seconds}', description: '遅延秒数' },
  { token: '{delay}', description: '{delay_seconds} と同じ意味' },
  { token: '{route_name}', description: '接続線の一般的な線名' },
  { token: '{route_id}', description: 'ATOS 線名コード' },
  { token: '{transfer_station}', description: '乗換駅名' },
];
